import {
  DATE_FORMAT,
  Flex,
  HyperLink,
  ThemeColors,
  Typography,
  dateFormat,
  padding,
} from '@evrekadev/evreka-ui-components'
import { AIActions, ActionType } from '../types'
import { useGetAnomalyAnalysisQuery } from 'services/ai'
import { useSelector } from 'react-redux'
import { selectClientId } from 'services/slices/auth'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo } from 'react'
import { AnomalyAnalysisResponse } from 'services/ai/types'
import styled from 'styled-components'
import { TabsV2 } from 'containers/TempTabs/TempTabs'
import { List } from '../components/List'
import { Award04Icon } from '../icons'
import { SquareSkeleton } from 'components/Skeletons/SquareSkeleton'
import { useAIContext } from '../context/AIProvider'
import { DataError } from '../components/DataError'
import { useTranslation } from 'utils/useTranslation'

const Alert02Icon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={24}
    height={24}
    color={'#000000'}
    fill={'none'}
    {...props}
  >
    <path
      d="M5.32171 9.6829C7.73539 5.41196 8.94222 3.27648 10.5983 2.72678C11.5093 2.42437 12.4907 2.42437 13.4017 2.72678C15.0578 3.27648 16.2646 5.41196 18.6783 9.6829C21.092 13.9538 22.2988 16.0893 21.9368 17.8293C21.7376 18.7866 21.2469 19.6548 20.535 20.3097C19.241 21.5 16.8274 21.5 12 21.5C7.17265 21.5 4.75897 21.5 3.46496 20.3097C2.75308 19.6548 2.26239 18.7866 2.06322 17.8293C1.70119 16.0893 2.90803 13.9538 5.32171 9.6829Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M11.992 16H12.001"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 13L12 8.99997"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const AnomalyAnalysis: React.FC = () => {
  const { t } = useTranslation('pages.ai.starters.anomaly_analysis')
  const clientId = useSelector(selectClientId)
  const { setData } = useAIContext()
  const { data, isLoading, isError } = useGetAnomalyAnalysisQuery(
    clientId ? { clientId } : skipToken,
  )

  const anomalyData = useMemo(() => {
    const idleServicePoints: AnomalyAnalysisResponse = []
    const anomalyServicePoints: AnomalyAnalysisResponse = []

    if (data) {
      data.forEach((anomaly) => {
        if (anomaly.type === 'idle') {
          idleServicePoints.push(anomaly)
        } else {
          anomalyServicePoints.push(anomaly)
        }
      })
    }
    return { idleServicePoints, anomalyServicePoints }
  }, [data])

  useEffect(() => {
    anomalyData && setData(JSON.stringify(anomalyData))
  }, [anomalyData])

  // const [deleteSP, { isLoading: isDeleteLoading }] = useDeleteServicePointMutation()

  // const onBulkDelete = () => {
  //   deleteSP({
  //     clientId,
  //     service_point_id_list: anomalyData.idleServicePoints.map((sp) => sp.id),
  //     filters: [],
  //     select_all: false,
  //   })
  //     .unwrap()
  //     .then(() => data && refetch())
  // }

  return (
    <div
      style={{
        maxHeight: '100%',
        overflowY: 'auto',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '1rem',
        flexGrow: 1,
      }}
    >
      {isLoading && (
        <Flex fullHeight directionColumn="zero" gap={1}>
          <SquareSkeleton height={34} width={200} />
          <SquareSkeleton height={'2rem'} width={'100%'} />
          <SquareSkeleton height={'2rem'} width={'100%'} />
          <SquareSkeleton height={'2rem'} width={'100%'} />
          <SquareSkeleton height={'2rem'} width={'100%'} />
          <SquareSkeleton height={'2rem'} width={'100%'} />
          <SquareSkeleton height={'2rem'} width={'100%'} />
          <SquareSkeleton height={'2rem'} width={'100%'} />
        </Flex>
      )}
      {isError && <DataError />}

      {data && (
        <>
          <TabsV2>
            <TabsV2.TabButton>{t('anomaly')}</TabsV2.TabButton>
            <TabsV2.TabButton>{t('idle')}</TabsV2.TabButton>
            <TabsV2.TabChild>
              {anomalyData.anomalyServicePoints.length === 0 ? (
                <EmptyList title={t('congratulations')} message={t('anomaly_empty')} />
              ) : (
                <List>
                  <ListRow>
                    <Typography fontSize="text_sm" fontWeight="medium" color="grey600">
                      {t('service_point')}
                    </Typography>
                    <Typography fontSize="text_sm" fontWeight="medium" color="grey600">
                      {t('last_task_generated')}
                    </Typography>
                  </ListRow>
                  {anomalyData.anomalyServicePoints.map((anomaly) => (
                    <ListRow key={anomaly.id}>
                      <HyperLink
                        to={`/ops_management/planning/service_points/detail/${anomaly.id}`}
                        value={anomaly.name}
                        target="_blank"
                      />

                      <Typography fontSize="text_sm">
                        {dateFormat(anomaly.latest_task_complete_date, DATE_FORMAT)}
                      </Typography>
                    </ListRow>
                  ))}
                </List>
              )}
            </TabsV2.TabChild>
            <TabsV2.TabChild>
              {anomalyData.idleServicePoints.length === 0 ? (
                <EmptyList title={t('congratulations')} message={t('idle_empty')} />
              ) : (
                <Flex directionColumn="zero" gap={1}>
                  <List>
                    <ListRow>
                      <Typography fontSize="text_sm" fontWeight="medium" color="grey600">
                        {t('service_point')}
                      </Typography>
                      <Typography fontSize="text_sm" fontWeight="medium" color="grey600">
                        {t('last_task_generated')}
                      </Typography>
                    </ListRow>
                    {anomalyData.idleServicePoints.map((anomaly) => (
                      <ListRow key={anomaly.id}>
                        <HyperLink
                          to={`/ops_management/planning/service_points/detail/${anomaly.id}`}
                          value={anomaly.name}
                          target="_blank"
                        />

                        <Typography fontSize="text_sm">
                          {dateFormat(anomaly.latest_task_complete_date, DATE_FORMAT)}
                        </Typography>
                      </ListRow>
                    ))}
                  </List>
                  {/* <Typography fontSize="text_md" fontWeight="semibold" color="grey600">
                    Suggestions
                  </Typography>
                  <Button
                    onClick={() => setDeleteModalOpen(true)}
                    color="danger"
                    iconLeft={UilTrashAlt}
                    loading={isDeleteLoading || isLoading}
                  >
                    Delete Idle Service Points
                  </Button> */}
                </Flex>
              )}
            </TabsV2.TabChild>
          </TabsV2>
        </>
      )}
      {/* <ConfirmModal
        handler={{
          onSubmit: onBulkDelete,
          isLoading: isDeleteLoading,
        }}
        variant="delete"
        isOpen={deleteModalOpen}
        onRequestClose={() => setDeleteModalOpen(false)}
        title="Delete Idle Service Points"
        message="Are you sure you want to delete these idle service points?"
      >
        {anomalyData.idleServicePoints.map((sp) => (
          <Typography variant="span" fontSize="text_sm" fontWeight="medium" color="grey600">
            {sp.name}
          </Typography>
        ))}
      </ConfirmModal> */}
    </div>
  )
}

const ListRow = styled.div`
  ${padding([4])}
  display: grid;
  grid-template-columns: 3fr 1fr;
  align-items: center;
  gap: 0.5rem;
`

const EmptyList: React.FC<{ title: string; message: string }> = ({ title, message }) => {
  return (
    <Flex
      style={{
        padding: '4rem',
        borderRadius: '8px',
        border: `1px solid ${ThemeColors.grey200}`,
      }}
      directionColumn="zero"
      gap={0.5}
      middle="zero"
      center="zero"
    >
      <Award04Icon height={64} width={64} color={ThemeColors.grey600} />
      <Typography fontSize="text_lg" fontWeight="semibold" color="grey600">
        {title}
      </Typography>
      <Typography fontSize="text_sm" fontWeight="medium" color="grey600">
        {message}
      </Typography>
    </Flex>
  )
}

export const anomalyAnalysis: ActionType = {
  action: AIActions.ANOMALY_ANALYSIS,
  title: 'anomaly_analysis.title',
  description: 'anomaly_analysis.description',
  icon: <Alert02Icon height={40} width={40} color={ThemeColors.grey700} />,
  dataComponent: <AnomalyAnalysis />,
}
